import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { customerService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Search, Loader2 } from "lucide-react";

const STATUS_COLOR = { Active: "green", Inactive: "slate", Prospect: "amber", Blocked: "red" };
const TYPES = ["Business", "Individual"];
const STATUSES = ["Active", "Inactive", "Prospect", "Blocked"];

const EMPTY = {
  customerName: "", customerType: "Business", status: "Active", email: "", mobile: "",
  industry: "", segment: "", loyaltyTier: "", notes: "", tenantId: "",
  billingAddress: { city: "", country: "" },
};

export default function CustomersPage() {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await customerService.list({ q: q || undefined, status: statusFilter || undefined, size: 100 });
      setRows(d.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {});
  }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...EMPTY, ...row, billingAddress: row.billingAddress || { city: "", country: "" } });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customerName) return toast.error("Customer name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        delete payload.tenantId; delete payload.customerCode;
        await customerService.update(editing.id, payload);
        toast.success("Customer updated");
      } else {
        const clean = Object.fromEntries(
          Object.entries(payload).filter(([k, v]) => v !== "" || k === "billingAddress"));
        await customerService.create(clean);
        toast.success("Customer created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.customerName}?`)) return;
    try {
      await customerService.remove(row.id);
      toast.success("Customer deleted");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    }
  };

  const canCreate = hasPermission("CUSTOMER.CREATE");
  const canEdit = hasPermission("CUSTOMER.EDIT");
  const canDelete = hasPermission("CUSTOMER.DELETE");

  const columns = [
    { key: "customerCode", header: "Code", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.customerCode}</span> },
    { key: "customerName", header: "Name", render: (r) => <span className="font-medium text-foreground">{r.customerName}</span> },
    { key: "customerType", header: "Type", render: (r) => <Badge color="slate">{r.customerType}</Badge> },
    { key: "status", header: "Status", render: (r) => <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "segment", header: "Segment", render: (r) => r.segment || "—" },
    { key: "email", header: "Email", render: (r) => <span className="text-muted-foreground text-xs">{r.email || "—"}</span> },
    {
      key: "_actions", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/customers/${r.id}`)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="Customer master — golden records across your tenants"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="customer-create-btn">
            <Plus className="h-4 w-4" /> New Customer
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 w-64">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, email…" className="bg-transparent text-sm outline-none w-full text-foreground" data-testid="customer-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="customer-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <DataTable columns={columns} rows={rows} loading={loading} testid="customers-table" empty="No customers yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Customer</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="customer-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Customer Name *"><input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} className="ciq-input" data-testid="customer-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type"><select value={form.customerType} onChange={(e) => setForm({ ...form, customerType: e.target.value })} className="ciq-input" data-testid="customer-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Status"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="ciq-input" data-testid="customer-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ciq-input" data-testid="customer-email" /></Field>
                <Field label="Mobile"><input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="ciq-input" data-testid="customer-mobile" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Industry"><input value={form.industry || ""} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="ciq-input" /></Field>
                <Field label="Segment"><input value={form.segment || ""} onChange={(e) => setForm({ ...form, segment: e.target.value })} className="ciq-input" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Billing City"><input value={form.billingAddress?.city || ""} onChange={(e) => setForm({ ...form, billingAddress: { ...form.billingAddress, city: e.target.value } })} className="ciq-input" /></Field>
                <Field label="Billing Country"><input value={form.billingAddress?.country || ""} onChange={(e) => setForm({ ...form, billingAddress: { ...form.billingAddress, country: e.target.value } })} className="ciq-input" /></Field>
              </div>
              <Field label="Notes"><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ciq-input" rows={2} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted" data-testid="customer-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="customer-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}
